import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Loader2,
  Pencil,
  Trash2,
  Upload,
  X,
  Save,
  ImageIcon,
  Eye,
  EyeOff,
} from "lucide-react";
import { API, useAuth } from "../AuthContext";
import {
  PRODUCT_CATEGORIES,
  PRODUCT_UNITS,
  CATEGORY_LABEL,
  resolveImageUrl,
} from "../productConstants";

const EMPTY_FORM = {
  name: "",
  category: "legumes",
  description: "",
  price: 0,
  unit: "unidade",
  available: true,
  is_seasonal: false,
  stock: "",
  sort_order: 0,
  image_url: null,
  image_path: null,
};

export default function ProductsPage() {
  const { authHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | productObj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders() });
      setProducts(data);
    } catch {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = products.filter((p) => {
    const matchCat = !filterCategory || p.category === filterCategory;
    const matchQ =
      !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchQ;
  });

  const toggleAvailable = async (p) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/products/${p.id}`,
        { available: !p.available },
        { headers: authHeaders() }
      );
      setProducts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    } catch {
      toast.error("Erro ao alterar disponibilidade");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers: authHeaders() });
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Produto eliminado");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  return (
    <div data-testid="admin-products-page" className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2C2C2C]/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar produto..."
                data-testid="products-search"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            data-testid="products-filter-category"
            className="px-3 py-2 rounded-md border border-[#D4C4A8] bg-[#F3F1EB]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
          >
            <option value="">Todas as categorias</option>
            {PRODUCT_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setEditing("new")}
            data-testid="products-new-btn"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors"
          >
            <Plus size={14} />
            Novo produto
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#3A4A38]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm p-12 text-center">
          <ImageIcon size={36} className="mx-auto text-[#2C2C2C]/30 mb-3" />
          <p className="text-sm text-[#2C2C2C]/60">
            {products.length === 0
              ? "Ainda não há produtos. Crie o primeiro!"
              : "Sem produtos para os filtros aplicados."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              onEdit={() => setEditing(p)}
              onDelete={() => onDelete(p)}
              onToggle={() => toggleAvailable(p)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ProductEditor
          product={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setProducts((prev) => {
              const exists = prev.some((x) => x.id === saved.id);
              return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev];
            });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// ============ CARD ============

function ProductCard({ product, onEdit, onDelete, onToggle }) {
  const imgUrl = resolveImageUrl(product.image_url);
  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={`bg-white rounded-xl border border-[#D4C4A8]/40 shadow-sm overflow-hidden flex flex-col ${
        !product.available ? "opacity-60" : ""
      }`}
    >
      <div className="relative aspect-[4/3] bg-[#F3F1EB] overflow-hidden">
        {imgUrl ? (
          <img src={imgUrl} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#2C2C2C]/30">
            <ImageIcon size={40} />
          </div>
        )}
        <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/85 text-[#3A4A38] font-medium">
          {CATEGORY_LABEL[product.category] || product.category}
        </span>
        {!product.available && (
          <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-800 font-medium">
            Indisponível
          </span>
        )}
        {product.is_seasonal && product.available && (
          <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
            Sazonal
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-[#2C2C2C] leading-tight">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-[#2C2C2C]/60 mt-1 line-clamp-2">{product.description}</p>
        )}
        <div className="mt-2 flex items-baseline gap-1">
          <span className="text-lg font-semibold text-[#3A4A38]">
            {Number(product.price).toFixed(2)}€
          </span>
          <span className="text-xs text-[#2C2C2C]/60">/ {product.unit}</span>
        </div>
        <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#D4C4A8]/30">
          <button
            onClick={onToggle}
            data-testid={`product-toggle-${product.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[#2C2C2C]/70 hover:bg-[#F3F1EB] transition-colors"
            title={product.available ? "Desativar" : "Ativar"}
          >
            {product.available ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button
            onClick={onEdit}
            data-testid={`product-edit-${product.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[#1B93A4] hover:bg-[#1B93A4]/10 transition-colors"
          >
            <Pencil size={14} />
            Editar
          </button>
          <button
            onClick={onDelete}
            data-testid={`product-delete-${product.id}`}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ EDITOR MODAL ============

function ProductEditor({ product, onClose, onSaved }) {
  const { authHeaders } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState(
    product
      ? {
          name: product.name,
          category: product.category,
          description: product.description || "",
          price: product.price,
          unit: product.unit,
          available: product.available,
          is_seasonal: !!product.is_seasonal,
          stock: product.stock ?? "",
          sort_order: product.sort_order ?? 0,
          image_url: product.image_url,
          image_path: product.image_path,
        }
      : EMPTY_FORM
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPickImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 5MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload/product-image`, fd, {
        headers: { ...authHeaders() },
      });
      setForm((f) => ({ ...f, image_url: data.url, image_path: data.path }));
      toast.success("Imagem carregada");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao carregar imagem");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setForm((f) => ({ ...f, image_url: null, image_path: null }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        description: form.description || null,
        price: Number(form.price) || 0,
        unit: form.unit,
        available: !!form.available,
        is_seasonal: !!form.is_seasonal,
        stock: form.stock === "" || form.stock === null ? null : Number(form.stock),
        sort_order: Number(form.sort_order) || 0,
        image_url: form.image_url,
        image_path: form.image_path,
      };
      let response;
      if (product) {
        response = await axios.patch(
          `${API}/admin/products/${product.id}`,
          payload,
          { headers: authHeaders() }
        );
      } else {
        response = await axios.post(
          `${API}/admin/products`,
          payload,
          { headers: authHeaders() }
        );
      }
      toast.success(product ? "Produto atualizado" : "Produto criado");
      onSaved(response.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const imgUrl = resolveImageUrl(form.image_url);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="product-editor-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-[#D4C4A8]/40 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {product ? "Editar produto" : "Novo produto"}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F3F1EB]">
            <X size={20} />
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {/* Image uploader */}
          <div>
            <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-2">Fotografia</label>
            <div className="flex items-start gap-4">
              <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-[#D4C4A8] bg-[#F3F1EB] flex-shrink-0">
                {uploading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    <Loader2 className="animate-spin text-[#3A4A38]" />
                  </div>
                ) : imgUrl ? (
                  <img src={imgUrl} alt="produto" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#2C2C2C]/30">
                    <ImageIcon size={28} />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => onPickImage(e.target.files?.[0])}
                  className="hidden"
                  data-testid="product-image-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="product-image-upload-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors disabled:opacity-60"
                >
                  <Upload size={14} />
                  {imgUrl ? "Mudar imagem" : "Carregar imagem"}
                </button>
                {imgUrl && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-red-600 hover:bg-red-50 text-sm transition-colors"
                  >
                    <Trash2 size={14} />
                    Remover
                  </button>
                )}
                <p className="text-[11px] text-[#2C2C2C]/50">JPG, PNG ou WEBP. Máx 5MB.</p>
              </div>
            </div>
          </div>

          {/* Basic fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Nome *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name"
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Categoria *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="product-category"
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Unidade *</label>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                data-testid="product-unit"
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              >
                {PRODUCT_UNITS.map((u) => (
                  <option key={u.key} value={u.key}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Preço (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="product-price"
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Stock (opcional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                placeholder="—"
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-[#2C2C2C]/70 mb-1">Descrição curta</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-5 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.available}
                onChange={(e) => setForm({ ...form, available: e.target.checked })}
                data-testid="product-available"
              />
              Disponível na loja
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_seasonal}
                onChange={(e) => setForm({ ...form, is_seasonal: e.target.checked })}
              />
              Sazonal
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-[#2C2C2C]/60 ml-auto">
              Ordem
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className="w-16 px-2 py-1 rounded border border-[#D4C4A8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3A4A38]"
              />
            </label>
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-[#D4C4A8]/40 flex items-center justify-end gap-2 bg-[#F3F1EB]/40">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-[#2C2C2C]/70 hover:bg-[#F3F1EB]">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving || uploading}
            data-testid="product-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#3A4A38] text-white text-sm hover:bg-[#2C3A2A] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </button>
        </footer>
      </div>
    </div>
  );
}
